import { Component } from '@theme/component';
import { onDocumentLoaded } from '@theme/utilities';

/**
 * Header web component.
 *
 * Responsibilities:
 * - Measure its own height and publish `--header-height` / `--header-group-height`
 *   on the document body so the rest of the theme can position under it.
 *
 * - Set `data-mega-open` when `header-menu` dispatches `menu:open`
 *   (desktop mega menu only, not the mobile `header-drawer` events) for the page dim;
 *   clear on `menu:close`.
 *
 * - Sticky header: Liquid adds `header--sticky`; CSS in `header.liquid` makes
 *   `#header-group` `position: sticky` via `:has(#header-component.header--sticky)`.
 *   `data-sticky-state="active"` is set in JS for `--sticky-header-offset`.
 *
 * - Transparent header: Liquid sets the `transparent` attribute permanently
 *   (never removed by JS). Visual state is driven by `data-scroll-direction`
 *   ("none" | "up" | "down"): CSS shows the transparent look only when
 *   `[data-scroll-direction='none']`. This keeps base.css's layout rule
 *   (negative margin on the first section) stable so the page never jumps.
 *
 * @extends {Component}
 */
class HeaderComponent extends Component {
  /** @type {ResizeObserver | null} */
  #resizeObserver = null;

  /** @type {boolean} */
  #isTransparentPage = false;

  /** @type {number | null} */
  #scrollRafId = null;

  /** @type {number} */
  #lastScrollY = 0;

  connectedCallback() {
    super.connectedCallback();

    this.#syncStickyStateDataset();

    this.#resizeObserver = new ResizeObserver(([entry]) => {
      if (!entry) return;
      const block = entry.borderBoxSize?.[0]?.blockSize ?? this.offsetHeight;
      document.body.style.setProperty('--header-height', `${Math.round(block)}px`);
    });
    this.#resizeObserver.observe(this);

    this.addEventListener('menu:open', this.#onMenuOpen);
    this.addEventListener('menu:close', this.#onMenuClose);

    // Scroll tracking: needed for transparent-header state and for
    // data-scroll-direction (used by the logo hide-on-home-page CSS).
    this.#isTransparentPage = this.hasAttribute('transparent');
    this.#lastScrollY = window.scrollY;

    const needsScrollTracking =
      this.#isTransparentPage || this.classList.contains('header--sticky');

    if (needsScrollTracking) {
      window.addEventListener('scroll', this.#onScroll, { passive: true });
      this.#updateScrollState();
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#resizeObserver?.disconnect();
    this.#resizeObserver = null;

    this.removeEventListener('menu:open', this.#onMenuOpen);
    this.removeEventListener('menu:close', this.#onMenuClose);

    window.removeEventListener('scroll', this.#onScroll);

    if (this.#scrollRafId != null) {
      cancelAnimationFrame(this.#scrollRafId);
      this.#scrollRafId = null;
    }

    delete this.dataset.stickyState;
    delete this.dataset.scrollDirection;
    document.body.style.setProperty('--header-height', '0px');
  }

  #syncStickyStateDataset() {
    if (this.classList.contains('header--sticky')) {
      this.dataset.stickyState = 'active';
    } else {
      delete this.dataset.stickyState;
    }
  }

  #onMenuOpen = (/** @type {Event} */ e) => {
    // Mobile drawer also dispatches menu:open; desktop dim is only for mega menu.
    if (e.target instanceof HTMLElement && e.target.nodeName === 'HEADER-DRAWER') {
      return;
    }
    this.dataset.megaOpen = 'true';
  };

  #onMenuClose = () => {
    delete this.dataset.megaOpen;
  };

  #onScroll = () => {
    if (this.#scrollRafId != null) return;
    this.#scrollRafId = requestAnimationFrame(() => {
      this.#scrollRafId = null;
      this.#updateScrollState();
    });
  };

  #updateScrollState() {
    const scrollY = window.scrollY;
    const atTop = scrollY === 0;

    // Keep data-scroll-direction in sync for CSS consumers.
    if (atTop) {
      this.dataset.scrollDirection = 'none';
    } else if (scrollY > this.#lastScrollY) {
      this.dataset.scrollDirection = 'down';
    } else {
      this.dataset.scrollDirection = 'up';
    }
    this.#lastScrollY = scrollY;
  }
}

if (!customElements.get('header-component')) {
  customElements.define('header-component', HeaderComponent);
}

/*
 * Publish the full header-group height (header + any sibling header sections
 * like the announcement bar) so content below can offset correctly.
 */
onDocumentLoaded(() => {
  const header = document.querySelector('header-component');
  const headerGroup = document.querySelector('#header-group');
  if (!(header instanceof HTMLElement) || !headerGroup) return;

  const publish = () => {
    let total = 0;
    for (const child of headerGroup.children) {
      if (child instanceof HTMLElement) total += child.offsetHeight;
    }
    document.body.style.setProperty('--header-group-height', `${Math.round(total)}px`);
  };

  const observer = new ResizeObserver(publish);
  for (const child of headerGroup.children) {
    if (child instanceof HTMLElement) observer.observe(child);
  }
  observer.observe(header);

  const mutationObserver = new MutationObserver(() => {
    for (const child of headerGroup.children) {
      if (child instanceof HTMLElement) observer.observe(child);
    }
    publish();
  });
  mutationObserver.observe(headerGroup, { childList: true });

  publish();
});
